import React from 'react';
import { OmgevingsData, MeldingBron, MeldingType, MELDING_BRON_LABELS, MELDING_TYPE_LABELS, WERK_CATEGORIE_LABELS, HINDER_TYPE_LABELS, WerkCategorie, HinderType } from '../../types';
import styles from './GebiedsanalyseModule.module.css';

const BRON_COLORS: Record<string, string> = { automatisch: '#277CEA', handmatig: '#9DA6A8' };
const TYPE_COLORS: Record<string, string> = { bewoner: '#8b5cf6', media: '#ec4899', gemeente: '#0ea5e9', monitoring: '#f59e0b' };

interface OmgevingsDataSummaryProps {
  data: OmgevingsData | null;
  onItemClick?: (lat: number, lng: number) => void;
}

export const OmgevingsDataSummary: React.FC<OmgevingsDataSummaryProps> = ({ data, onItemClick }) => {
  if (!data) return null;

  const { meldingen, werkzaamheden, vergunningen } = data;
  const totalItems = meldingen.length + werkzaamheden.length + vergunningen.length;

  if (totalItems === 0) return null;

  const openMeldingen = meldingen.filter(m => m.status === 'open').length;
  const recentVergunningen = vergunningen.slice(0, 3);

  // Bereken meldingen-statistieken per bron en type
  const bronStats: Record<string, number> = {};
  const typeStats: Record<string, number> = {};
  meldingen.forEach(m => {
    if (m.bron) bronStats[m.bron] = (bronStats[m.bron] || 0) + 1;
    if (m.meldingType) typeStats[m.meldingType] = (typeStats[m.meldingType] || 0) + 1;
  });

  return (
    <div className={styles.section}>
      <h3>Omgevingsdata</h3>

      {/* Summary stats */}
      <div className={styles.omgevingsSummaryGrid}>
        <div className={styles.omgevingsSummaryStat}>
          <span className={styles.omgevingsSummaryValue} style={{ color: '#ef4444' }}>
            {meldingen.length}
          </span>
          <span className={styles.omgevingsSummaryLabel}>
            Meldingen{openMeldingen > 0 ? ` (${openMeldingen} open)` : ''}
          </span>
        </div>
        <div className={styles.omgevingsSummaryStat}>
          <span className={styles.omgevingsSummaryValue} style={{ color: '#f97316' }}>
            {werkzaamheden.length}
          </span>
          <span className={styles.omgevingsSummaryLabel}>Werkzaamheden</span>
        </div>
        <div className={styles.omgevingsSummaryStat}>
          <span className={styles.omgevingsSummaryValue} style={{ color: '#10b981' }}>
            {vergunningen.length}
          </span>
          <span className={styles.omgevingsSummaryLabel}>Vergunningen</span>
        </div>
      </div>

      {/* Meldingen met transparantie-info */}
      {meldingen.length > 0 && (
        <div className={styles.omgevingsSubSection}>
          <h4 className={styles.omgevingsSubTitle}>Meldingen</h4>

          {/* Bron & type breakdown */}
          <div className={styles.meldingTransparantie}>
            {Object.keys(bronStats).length > 0 && (
              <div className={styles.meldingMetaRow}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#9DA6A8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 2L2 7l10 5 10-5-10-5z" /><path d="M2 17l10 5 10-5" /><path d="M2 12l10 5 10-5" />
                </svg>
                <span className={styles.meldingMetaLabel}>Bron:</span>
                <div className={styles.meldingMetaTags}>
                  {Object.entries(bronStats).map(([bron, count]) => (
                    <span key={bron} className={styles.meldingMetaTag} style={{ borderColor: BRON_COLORS[bron] || '#9DA6A8', color: BRON_COLORS[bron] || '#9DA6A8' }}>
                      {MELDING_BRON_LABELS[bron as MeldingBron] || bron} ({count})
                    </span>
                  ))}
                </div>
              </div>
            )}
            {Object.keys(typeStats).length > 0 && (
              <div className={styles.meldingMetaRow}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#9DA6A8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" />
                </svg>
                <span className={styles.meldingMetaLabel}>Type:</span>
                <div className={styles.meldingMetaTags}>
                  {Object.entries(typeStats).map(([type, count]) => (
                    <span key={type} className={styles.meldingMetaTag} style={{ borderColor: TYPE_COLORS[type] || '#9DA6A8', color: TYPE_COLORS[type] || '#9DA6A8' }}>
                      {MELDING_TYPE_LABELS[type as MeldingType] || type} ({count})
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Meldingenlijst */}
          {meldingen.slice(0, 5).map((melding, i) => {
            const statusColor = melding.status === 'open' ? '#ef4444' : melding.status === 'in_behandeling' ? '#f59e0b' : '#22c55e';
            const statusLabel = melding.status === 'open' ? 'Open' : melding.status === 'in_behandeling' ? 'In behandeling' : 'Afgehandeld';
            return (
              <button
                key={i}
                className={styles.omgevingsListItem}
                onClick={() => melding.lat && melding.lng && onItemClick?.(melding.lat, melding.lng)}
              >
                <span className={styles.omgevingsStatusDot} style={{ backgroundColor: statusColor }} />
                <div className={styles.omgevingsListContent}>
                  <span className={styles.omgevingsListTitle}>{melding.text}</span>
                  <div className={styles.meldingListMeta}>
                    <span className={styles.meldingListBadge} style={{ color: statusColor }}>{statusLabel}</span>
                    {melding.meldingType && (
                      <span className={styles.meldingListBadge} style={{ color: TYPE_COLORS[melding.meldingType] || '#9DA6A8' }}>
                        {MELDING_TYPE_LABELS[melding.meldingType as MeldingType] || melding.meldingType}
                      </span>
                    )}
                    {melding.bron && (
                      <span className={styles.meldingListBadge} style={{ color: BRON_COLORS[melding.bron] || '#9DA6A8' }}>
                        {melding.bron === 'automatisch' ? 'Auto' : 'Handmatig'}
                      </span>
                    )}
                    <span className={styles.meldingListDate}>
                      {new Date(melding.createdAt).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' })}
                    </span>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* Werkzaamheden met project/raakvlak onderscheid */}
      {werkzaamheden.length > 0 && (() => {
        const projectWerk = werkzaamheden.filter(w => w.werkCategorie === 'project');
        const raakvlakWerk = werkzaamheden.filter(w => w.werkCategorie !== 'project');

        // Hinderanalyse: tel hindertypes
        const hinderStats: Record<string, number> = {};
        werkzaamheden.forEach(w => {
          if (w.hinderType) hinderStats[w.hinderType] = (hinderStats[w.hinderType] || 0) + 1;
        });

        return (
          <div className={styles.omgevingsSubSection}>
            <h4 className={styles.omgevingsSubTitle}>Werkzaamheden</h4>

            {/* Categorie breakdown */}
            <div className={styles.meldingTransparantie}>
              <div className={styles.meldingMetaRow}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#9DA6A8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="7" width="20" height="14" rx="2" /><path d="M16 3h-8l-2 4h12l-2-4z" />
                </svg>
                <span className={styles.meldingMetaLabel}>Type:</span>
                <div className={styles.meldingMetaTags}>
                  {projectWerk.length > 0 && (
                    <span className={styles.meldingMetaTag} style={{ borderColor: '#1E3A2F', color: '#1E3A2F' }}>
                      Project ({projectWerk.length})
                    </span>
                  )}
                  {raakvlakWerk.length > 0 && (
                    <span className={styles.meldingMetaTag} style={{ borderColor: '#f97316', color: '#f97316' }}>
                      Raakvlak ({raakvlakWerk.length})
                    </span>
                  )}
                </div>
              </div>
              {Object.keys(hinderStats).length > 0 && (
                <div className={styles.meldingMetaRow}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#9DA6A8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                    <line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
                  </svg>
                  <span className={styles.meldingMetaLabel}>Hinder:</span>
                  <div className={styles.meldingMetaTags}>
                    {Object.entries(hinderStats).map(([type, count]) => (
                      <span key={type} className={styles.meldingMetaTag} style={{ borderColor: '#ef4444', color: '#ef4444' }}>
                        {HINDER_TYPE_LABELS[type as HinderType] || type} ({count})
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Raakvlakprojecten eerst (met oranje accent) */}
            {raakvlakWerk.length > 0 && raakvlakWerk.slice(0, 3).map((werk, i) => (
              <button
                key={`rv-${i}`}
                className={styles.omgevingsListItem}
                onClick={() => werk.lat && werk.lng && onItemClick?.(werk.lat, werk.lng)}
              >
                <span className={styles.omgevingsStatusDot} style={{ backgroundColor: '#f97316' }} />
                <div className={styles.omgevingsListContent}>
                  <span className={styles.omgevingsListTitle}>{werk.description}</span>
                  <div className={styles.meldingListMeta}>
                    <span className={styles.meldingListBadge} style={{ color: '#f97316' }}>Raakvlak</span>
                    <span className={styles.meldingListBadge} style={{ color: '#9DA6A8' }}>{werk.source}</span>
                    {werk.hinderType && (
                      <span className={styles.meldingListBadge} style={{ color: '#ef4444' }}>
                        {HINDER_TYPE_LABELS[werk.hinderType as HinderType] || werk.hinderType}
                      </span>
                    )}
                    {werk.startDate && (
                      <span className={styles.meldingListDate}>
                        {new Date(werk.startDate).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' })}
                      </span>
                    )}
                    {werk.url && (
                      <a
                        href={werk.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={styles.omgevingsVergunningLink}
                        onClick={(e) => e.stopPropagation()}
                      >
                        Bekijken
                      </a>
                    )}
                  </div>
                </div>
              </button>
            ))}

            {/* Projectwerkzaamheden */}
            {projectWerk.length > 0 && projectWerk.slice(0, 3).map((werk, i) => (
              <button
                key={`pw-${i}`}
                className={styles.omgevingsListItem}
                onClick={() => werk.lat && werk.lng && onItemClick?.(werk.lat, werk.lng)}
              >
                <span className={styles.omgevingsStatusDot} style={{ backgroundColor: '#1E3A2F' }} />
                <div className={styles.omgevingsListContent}>
                  <span className={styles.omgevingsListTitle}>{werk.description}</span>
                  <div className={styles.meldingListMeta}>
                    <span className={styles.meldingListBadge} style={{ color: '#1E3A2F' }}>Project</span>
                    <span className={styles.meldingListBadge} style={{ color: '#9DA6A8' }}>{werk.source}</span>
                    {werk.hinderType && (
                      <span className={styles.meldingListBadge} style={{ color: '#ef4444' }}>
                        {HINDER_TYPE_LABELS[werk.hinderType as HinderType] || werk.hinderType}
                      </span>
                    )}
                    {werk.startDate && (
                      <span className={styles.meldingListDate}>
                        {new Date(werk.startDate).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' })}
                      </span>
                    )}
                    {werk.url && (
                      <a
                        href={werk.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={styles.omgevingsVergunningLink}
                        onClick={(e) => e.stopPropagation()}
                      >
                        Bekijken
                      </a>
                    )}
                  </div>
                </div>
              </button>
            ))}
          </div>
        );
      })()}

      {/* Vergunningen */}
      {recentVergunningen.length > 0 && (
        <div className={styles.omgevingsSubSection}>
          <h4 className={styles.omgevingsSubTitle}>Recente vergunningen</h4>
          {recentVergunningen.map((verg, i) => (
            <div key={i} className={styles.omgevingsVergunningItem}>
              <span className={styles.omgevingsVergunningType}>{verg.type}</span>
              <p className={styles.omgevingsVergunningTitle}>{verg.title}</p>
              <div className={styles.omgevingsVergunningMeta}>
                {verg.date && (
                  <span>{new Date(verg.date).toLocaleDateString('nl-NL')}</span>
                )}
                {verg.url && (
                  <a href={verg.url} target="_blank" rel="noopener noreferrer" className={styles.omgevingsVergunningLink}>
                    Bekijken
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
